import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Check, Crop, Send, ZoomIn } from 'lucide-react';
import { Button } from '../ui/button';
import { PleinEcran, Alertes, Traitement, tailleKo } from './PleinEcran';
import { preparerPage, base64EnFichier } from './scanUtils';

const clair = 'border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white';
const vert = 'bg-[#8DC63F] text-[#0F172A] hover:bg-[#7AB534]';
const borner = (v, min, max) => Math.min(max, Math.max(min, v));

// Cadre 35 × 45 mm fixe : le candidat déplace / zoome sa photo derrière l'ovale
const Cadrage = ({ page, initial, onValider, onReprendre }) => {
  const L = Math.min(300, window.innerWidth - 64);
  const H = Math.round((L * 9) / 7);
  const base = Math.max(L / page.w, H / page.h);
  const [zoom, setZoom] = useState(initial?.zoom || 1);
  const [pos, setPos] = useState(initial?.pos || { x: (L - page.w * base) / 2, y: (H - page.h * base) / 2 });
  const debut = useRef(null);
  const iw = page.w * base * zoom, ih = page.h * base * zoom;
  const caler = (p, w = iw, h = ih) => ({ x: borner(p.x, L - w, 0), y: borner(p.y, H - h, 0) });
  const zoomer = (z) => {
    const r = z / zoom;
    setPos(caler({ x: L / 2 - (L / 2 - pos.x) * r, y: H / 2 - (H / 2 - pos.y) * r }, page.w * base * z, page.h * base * z));
    setZoom(z);
  };
  const cadre = { x: -pos.x / iw, y: -pos.y / ih, w: L / iw, h: H / ih };
  return (
    <>
      <div className="relative touch-none select-none overflow-hidden rounded-xl" style={{ width: L, height: H }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); debut.current = { cx: e.clientX, cy: e.clientY, ...pos }; }}
        onPointerMove={(e) => debut.current && setPos(caler({ x: debut.current.x + e.clientX - debut.current.cx, y: debut.current.y + e.clientY - debut.current.cy }))}
        onPointerUp={() => { debut.current = null; }} data-testid="photo-cadrage">
        <img src={page.url} alt="Selfie" draggable={false} style={{ position: 'absolute', left: pos.x, top: pos.y, width: iw, height: ih, maxWidth: 'none' }} />
        <svg viewBox="0 0 70 90" className="pointer-events-none absolute inset-0 h-full w-full">
          <path d="M0 0H70V90H0Z M35 12 a20 26 0 1 0 0.01 0Z" fillRule="evenodd" fill="rgba(15,23,42,0.5)" />
          <ellipse cx="35" cy="38" rx="20" ry="26" fill="none" stroke="#8DC63F" strokeWidth="0.8" strokeDasharray="2 1.5" />
        </svg>
      </div>
      <p className="mt-3 max-w-xs text-center text-xs text-white/70">Visage dans l'ovale, de face, épaules visibles, fond clair et uni, sans lunettes de soleil ni flash.</p>
      <label className="mt-3 flex w-full max-w-xs items-center gap-3 text-sm text-white/80">
        <ZoomIn className="h-4 w-4" />
        <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => zoomer(Number(e.target.value))} className="flex-1 accent-[#8DC63F]" data-testid="photo-zoom" />
      </label>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" className={clair} onClick={onReprendre} data-testid="photo-reprendre"><Camera className="h-4 w-4 mr-2" /> Reprendre</Button>
        <Button className={vert} onClick={() => onValider(cadre, { zoom, pos })} data-testid="photo-valider-cadrage"><Check className="h-4 w-4 mr-2" /> Valider</Button>
      </div>
    </>
  );
};

export const PhotoIdentiteDialog = ({ doc, premiere, onClose, onFichier }) => {
  const [page, setPage] = useState(premiere);
  const [resultat, setResultat] = useState(null);
  const [etape, setEtape] = useState('cadrage');
  const [busy, setBusy] = useState(false);
  const [reglage, setReglage] = useState(null);
  const input = useRef(null);

  const valider = async (cadre, etat) => {
    setReglage(etat); setEtape('apercu'); setResultat(null); setBusy(true);
    const fd = new FormData();
    fd.append('mode', 'photo');
    fd.append('params', JSON.stringify([{ cadre }]));
    fd.append('pages', page.blob, 'photo.jpg');
    try { setResultat((await axios.post('/scan/apercu', fd, { timeout: 60000 })).data); } catch (e) {
      toast.error(e.response?.data?.detail || 'Traitement impossible, reprenez la photo'); setEtape('cadrage');
    } finally { setBusy(false); }
  };
  const reprendre = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try { setPage(await preparerPage(f)); setReglage(null); setEtape('cadrage'); } catch { toast.error('Photo illisible, réessayez'); }
  };
  const envoyer = async () => {
    setBusy(true);
    try { await onFichier(base64EnFichier(resultat.fichier_base64, 'photo_identite.jpg', 'image/jpeg')); onClose(); } finally { setBusy(false); }
  };
  return (
    <PleinEcran titre={doc.nom} sousTitre={etape === 'cadrage' ? 'Format 35 × 45 mm : déplacez et zoomez la photo' : resultat ? `JPG couleur 600 × 771 px · ${tailleKo(resultat.taille)}` : 'Recadrage et contrôle qualité'}
      onClose={onClose} testId="photo-identite"
      actions={etape === 'apercu' && resultat && <>
        <Button variant="outline" className={clair} onClick={() => setEtape('cadrage')} disabled={busy} data-testid="photo-recadrer"><Crop className="h-4 w-4 mr-2" /> Recadrer</Button>
        <Button className={vert} onClick={envoyer} disabled={busy} data-testid="photo-envoyer"><Send className="h-4 w-4 mr-2" /> Ajouter à mon dossier</Button>
      </>}>
      {etape === 'cadrage' ? <Cadrage key={page.url} page={page} initial={reglage} onValider={valider} onReprendre={() => input.current?.click()} /> : busy && !resultat ? <Traitement texte="Recadrage et contrôle…" /> : resultat && (
        <div className="flex w-full max-w-xs flex-col items-center gap-3">
          <img src={`data:image/jpeg;base64,${resultat.apercu_base64}`} alt="Photo d'identité" className="w-52 rounded-md shadow-2xl" data-testid="photo-apercu-image" />
          <Alertes alertes={resultat.alertes} />
        </div>
      )}
      <input ref={input} type="file" accept="image/*" capture="user" className="hidden" onChange={reprendre} data-testid="photo-input" />
    </PleinEcran>
  );
};
